import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    Compass,
    Globe2,
    Landmark,
    Sparkles,
} from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import Reveal from "@/components/Reveal";
import { useContent, DEFAULT_CONTENT } from "@/lib/content";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";

// Authentic Monaco / Côte d'Azur imagery via Pexels
const HERO_IMAGE =
    "https://images.pexels.com/photos/14079666/pexels-photo-14079666.jpeg?auto=compress&cs=tinysrgb&w=2400"; // Monaco-Ville aerial coastline with Mediterranean
const RIVIERA_IMAGE =
    "https://images.pexels.com/photos/30608241/pexels-photo-30608241.jpeg?auto=compress&cs=tinysrgb&w=2400"; // Monaco marina with yachts
const YACHT_IMAGE =
    "https://images.pexels.com/photos/22940100/pexels-photo-22940100.jpeg?auto=compress&cs=tinysrgb&w=2400"; // Monaco luxury marina aerial
const MARBLE_IMAGE =
    "https://images.pexels.com/photos/30297108/pexels-photo-30297108.jpeg?auto=compress&cs=tinysrgb&w=2400"; // Monaco Belle Époque architecture

const services = [
    {
        icon: Compass,
        kicker: "I",
        title: "Architecture financière & patrimoniale",
        desc: "Conception, à la mesure de chaque dirigeant, d'une architecture financière et patrimoniale cohérente — coordonnée, discrète, pensée pour le long terme.",
    },
    {
        icon: Landmark,
        kicker: "II",
        title: "Opérations de croissance & transmission",
        desc: "Accompagnement stratégique des opérations de développement, de croissance et de transmission — préservation de la gouvernance et de la vision du dirigeant.",
    },
    {
        icon: Sparkles,
        kicker: "III",
        title: "Ingénierie de financement & structuration",
        desc: "Solutions de financement adaptées aux enjeux patrimoniaux et entrepreneuriaux — coordination d'experts indépendants et de partenaires internationaux.",
    },
    {
        icon: Globe2,
        kicker: "IV",
        title: "Réseau international d'experts",
        desc: "Coordination d'un écosystème international d'experts et de finance privée — mobilisé, mandat par mandat, dans une logique de cercle restreint et confidentiel.",
    },
];

function Counter({ to, suffix = "", duration = 2200, prefix = "" }) {
    const [value, setValue] = useState(0);
    const ref = useRef(null);
    const started = useRef(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting && !started.current) {
                        started.current = true;
                        const start = performance.now();
                        const tick = (now) => {
                            const p = Math.min(1, (now - start) / duration);
                            const eased = 1 - Math.pow(1 - p, 3);
                            setValue(Math.round(to * eased));
                            if (p < 1) requestAnimationFrame(tick);
                        };
                        requestAnimationFrame(tick);
                    }
                });
            },
            { threshold: 0.3 }
        );
        obs.observe(node);
        return () => obs.disconnect();
    }, [to, duration]);

    return (
        <span ref={ref} className="tabular-nums">
            {prefix}
            {value}
            {suffix}
        </span>
    );
}

export default function Home() {
    const content = useContent();
    const { t, lang } = useI18n();
    const hero = { ...DEFAULT_CONTENT.hero, ...(content.hero || {}) };
    const stats = { ...DEFAULT_CONTENT.stats, ...(content.stats || {}) };
    const cities = content.cities?.list?.length
        ? content.cities.list
        : DEFAULT_CONTENT.cities.list;
    const [articles, setArticles] = useState([]);

    // Pour les champs dont la copie a été réécrite, on utilise `t()`
    // directement (dictionnaire FR/EN garanti) plutôt que `hero.subtitle`
    // qui pourrait encore refléter l'ancien texte stocké en base via le CMS.
    const tr = (key, fr) => (lang === "en" ? t(key, fr) : fr);

    useEffect(() => {
        api.get("/articles")
            .then(({ data }) => setArticles(data.slice(0, 2)))
            .catch(() => setArticles([]));
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section
                data-testid="hero"
                className="relative h-[100svh] min-h-[680px] w-full overflow-hidden bg-xv-black"
            >
                <div className="absolute inset-0">
                    <img
                        src={HERO_IMAGE}
                        alt="Côte d'Azur · Monaco"
                        className="w-full h-full object-cover animate-slow-zoom"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/90" />
                </div>

                <div className="relative h-full max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-20 md:pb-32">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            duration: 1.4,
                            ease: [0.16, 1, 0.3, 1],
                            delay: 0.3,
                        }}
                        className="max-w-5xl"
                    >
                        <div className="flex items-center gap-4 mb-8 text-xv-goldSoft">
                            <span className="divider-gold" />
                            <span className="overline">
                                {tr("home.hero.badge", hero.badge)}
                            </span>
                        </div>
                        <h1
                            className="font-serif text-xv-ivory text-[44px] sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.98] tracking-tight"
                            style={{ fontWeight: 300 }}
                        >
                            {tr("home.hero.title", hero.title)}
                            <br />
                            <em className="not-italic text-xv-goldSoft font-serif">
                                {tr("home.hero.titleEmphasis", hero.titleEmphasis)}
                            </em>
                        </h1>
                        <p className="mt-10 text-xv-ivory/85 max-w-xl text-[17px] md:text-lg leading-relaxed font-light whitespace-pre-line">
                            {t("home.hero.subtitle", hero.subtitle)}
                        </p>
                        <div className="mt-12 flex flex-col sm:flex-row gap-4 sm:gap-6">
                            <Link
                                to="/contact"
                                data-testid="hero-cta-contact"
                                className="group inline-flex items-center gap-4 px-8 py-4 bg-xv-ivory text-xv-midnightDeep text-[11px] tracking-[0.3em] uppercase hover:bg-xv-gold transition-all duration-700"
                            >
                                {tr("home.cta1", hero.cta1)}
                                <ArrowUpRight
                                    strokeWidth={1.2}
                                    size={16}
                                    className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                                />
                            </Link>
                            <Link
                                to="/approche"
                                data-testid="hero-cta-about"
                                className="inline-flex items-center gap-4 px-8 py-4 border border-xv-ivory/40 text-xv-ivory text-[11px] tracking-[0.3em] uppercase hover:border-xv-ivory transition-all duration-700"
                            >
                                {tr("home.cta2", hero.cta2)}
                            </Link>
                        </div>
                    </motion.div>
                </div>

                <div className="absolute bottom-6 right-6 md:right-12 hidden md:flex items-center gap-3 text-xv-ivory/60 text-[10px] tracking-[0.3em] uppercase rotate-90 origin-bottom-right translate-y-[-40px]">
                    <span className="w-12 h-px bg-xv-ivory/40" />
                    Scroll
                </div>
            </section>

            {/* MANIFESTO */}
            <section
                data-testid="manifesto"
                className="relative bg-xv-ivory py-28 md:py-40"
            >
                <div className="max-w-[1100px] mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-10 text-xv-gold">
                            <span className="divider-gold" />
                            <span className="overline">
                                {tr("home.manifesto.kicker", content.manifesto?.kicker || "Notre conviction")}
                            </span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2
                            className="font-serif text-xv-black text-[34px] md:text-5xl lg:text-[58px] leading-[1.05]"
                            style={{ fontWeight: 300 }}
                        >
                            {tr("home.manifesto.line1", content.manifesto?.line1 || "Les opérations qui marquent")}{" "}
                            {tr("home.manifesto.line2", content.manifesto?.line2 || "ne se traitent ni seul,")}{" "}
                            <em className="not-italic text-xv-midnight">
                                {tr("home.manifesto.line3", content.manifesto?.line3 || "ni en série.")}
                            </em>
                        </h2>
                    </Reveal>
                    <div className="grid md:grid-cols-2 gap-12 md:gap-20 mt-20">
                        <Reveal delay={0.05}>
                            <p
                                className="font-serif text-2xl md:text-[26px] leading-relaxed text-xv-text-secondary"
                                style={{ fontWeight: 300 }}
                            >
                                {tr(
                                    "home.manifesto.lead",
                                    "Une architecture juridique, fiscale et financière calibrée à la mesure d'une ambition.",
                                )}
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="text-xv-text-secondary text-[16px] md:text-[17px] leading-[1.85] font-light">
                                {t(
                                    "home.manifesto.p1",
                                    "XV Capital Partners coordonne, autour de chaque opération, un cercle restreint d'experts — droit des sociétés, fiscalité patrimoniale et corporate, audit, due diligence, gouvernance, relations investisseurs — sélectionnés pour la discrétion et la rigueur de leur exécution.",
                                )}
                                <br />
                                <br />
                                {t(
                                    "home.manifesto.p2",
                                    "Une préparation rigoureuse, une coordination réactive et un calendrier adapté aux enjeux de chaque opération.",
                                )}
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* STATS — Riviera band */}
            <section
                data-testid="stats"
                className="relative bg-xv-midnightDeep text-xv-ivory overflow-hidden"
            >
                <div className="absolute inset-0 opacity-25 pointer-events-none">
                    <img
                        src={RIVIERA_IMAGE}
                        alt=""
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-xv-midnightDeep/75" />
                </div>
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-16 text-xv-goldSoft">
                            <span className="divider-gold" />
                            <span className="overline">
                                {stats.kicker}
                            </span>
                        </div>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-8">
                        {stats.items.map((s, i) => (
                            <Reveal key={s.label} delay={0.1 * i}>
                                <div className="border-t border-xv-goldSoft/30 pt-8">
                                    <div
                                        className="font-serif text-6xl md:text-7xl lg:text-[88px] leading-none text-xv-ivory"
                                        style={{ fontWeight: 300 }}
                                    >
                                        <Counter
                                            to={s.value}
                                            suffix={s.suffix}
                                        />
                                    </div>
                                    <p className="overline text-xv-goldSoft mt-6">
                                        {s.label}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section
                data-testid="services"
                className="bg-xv-ivory py-28 md:py-40"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid lg:grid-cols-12 gap-12 mb-20">
                        <div className="lg:col-span-5">
                            <Reveal>
                                <div className="flex items-center gap-4 mb-8 text-xv-gold">
                                    <span className="divider-gold" />
                                    <span className="overline">
                                        Métiers
                                    </span>
                                </div>
                                <h2
                                    className="font-serif text-xv-black text-[40px] md:text-5xl lg:text-[64px] leading-[1.02]"
                                    style={{ fontWeight: 300 }}
                                >
                                    Une intervention,
                                    <br />
                                    <em className="not-italic text-xv-midnight">
                                        quatre dimensions.
                                    </em>
                                </h2>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 self-end">
                            <Reveal delay={0.1}>
                                <p className="text-xv-text-secondary text-[16px] md:text-[17px] leading-[1.85] font-light">
                                    Chaque mission combine, à des degrés
                                    différents, ces quatre dimensions —
                                    coordonnées en interne et adossées à
                                    un réseau d'experts indépendants.
                                </p>
                            </Reveal>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-px bg-xv-line">
                        {services.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <Reveal
                                    key={s.title}
                                    delay={i * 0.08}
                                    className="bg-xv-ivory"
                                >
                                    <div
                                        data-testid={`service-${i}`}
                                        className="group p-10 md:p-14 h-full bg-xv-ivory transition-colors duration-700 hover:bg-xv-cream cursor-default"
                                    >
                                        <div className="flex items-start justify-between mb-10">
                                            <span className="overline text-xv-gold">
                                                {s.kicker}
                                            </span>
                                            <Icon
                                                strokeWidth={1}
                                                size={28}
                                                className="text-xv-midnight transition-transform duration-700 group-hover:rotate-[8deg]"
                                            />
                                        </div>
                                        <h3
                                            className="font-serif text-3xl md:text-4xl text-xv-black mb-6"
                                            style={{ fontWeight: 400 }}
                                        >
                                            {s.title}
                                        </h3>
                                        <p className="text-xv-text-secondary text-[15px] md:text-[16px] leading-[1.85] font-light max-w-md">
                                            {s.desc}
                                        </p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>

                    <div className="mt-16 flex justify-end">
                        <Reveal delay={0.2}>
                            <Link
                                to="/expertises"
                                data-testid="home-expertises-link"
                                className="inline-flex items-center gap-3 text-xv-midnight text-[11px] tracking-[0.28em] uppercase link-underline"
                            >
                                {tr("home.ctaExpertises", "Découvrir nos expertises")}
                                <ArrowUpRight strokeWidth={1.2} size={16} />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* COLLECTIVITES SPOTLIGHT */}
            <section
                data-testid="collectivites-spotlight"
                className="relative bg-xv-black text-xv-ivory overflow-hidden"
            >
                <div className="absolute inset-0 opacity-30">
                    <img
                        src={MARBLE_IMAGE}
                        alt=""
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-xv-black/85 via-xv-black/65 to-xv-black/90" />
                </div>
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-28 md:py-40 grid lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <div className="flex items-center gap-4 mb-10 text-xv-goldSoft">
                                <span className="divider-gold" />
                                <span className="overline">
                                    Acteurs institutionnels & territoires
                                </span>
                            </div>
                            <h2
                                className="font-serif text-[40px] md:text-5xl lg:text-[64px] leading-[1.04] mb-10"
                                style={{ fontWeight: 300 }}
                            >
                                Accompagner les{" "}
                                <em className="not-italic text-xv-goldSoft">
                                    territoires.
                                </em>
                            </h2>
                            <p className="text-xv-ivory/80 text-[16px] md:text-[18px] leading-[1.85] font-light mb-12 max-w-lg">
                                Pour les acteurs publics et institutionnels, le
                                cabinet coordonne un écosystème
                                international d'experts et de partenaires
                                financiers de long terme — dédié à la
                                structuration d'opérations d'envergure au
                                service du développement territorial.
                            </p>
                            <Link
                                to="/collectivites"
                                data-testid="home-collectivites-cta"
                                className="group inline-flex items-center gap-4 px-8 py-4 border border-xv-ivory/40 text-xv-ivory text-[11px] tracking-[0.3em] uppercase hover:bg-xv-ivory hover:text-xv-black transition-all duration-700"
                            >
                                Découvrir l'approche
                                <ArrowUpRight
                                    strokeWidth={1.2}
                                    size={16}
                                    className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                                />
                            </Link>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <Reveal delay={0.15}>
                            <div className="grid grid-cols-2 gap-px bg-xv-ivory/15">
                                {[
                                    { v: "Long terme", l: "Horizon" },
                                    { v: "International", l: "Réseau" },
                                    { v: "Sélectif", l: "Mandats" },
                                    { v: "Confidentiel", l: "Mode opératoire" },
                                ].map((k) => (
                                    <div
                                        key={k.l}
                                        className="bg-xv-black p-8 md:p-10 flex flex-col justify-end min-h-[170px]"
                                    >
                                        <div
                                            className="font-serif text-[28px] md:text-[40px] leading-none text-xv-ivory"
                                            style={{ fontWeight: 300 }}
                                        >
                                            {k.v}
                                        </div>
                                        <p className="overline text-xv-goldSoft mt-5">
                                            {k.l}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* INTERNATIONAL PRESENCE */}
            <section
                data-testid="presence"
                className="relative bg-xv-cream py-28 md:py-40 overflow-hidden"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    <div className="lg:col-span-6 order-2 lg:order-1">
                        <Reveal>
                            <img
                                src={YACHT_IMAGE}
                                alt="Présence internationale"
                                className="w-full h-[460px] md:h-[620px] object-cover"
                            />
                        </Reveal>
                    </div>
                    <div className="lg:col-span-6 order-1 lg:order-2">
                        <Reveal>
                            <div className="flex items-center gap-4 mb-8 text-xv-gold">
                                <span className="divider-gold" />
                                <span className="overline">
                                    {tr("home.presence.kicker", "Présence internationale")}
                                </span>
                            </div>
                            <h2
                                className="font-serif text-xv-black text-[40px] md:text-5xl lg:text-[60px] leading-[1.04] mb-10"
                                style={{ fontWeight: 300 }}
                            >
                                {t("home.presence.title1", "Un cabinet indépendant,")}{" "}
                                <em className="not-italic text-xv-midnight">
                                    {t("home.presence.title2", "un réseau international.")}
                                </em>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="text-xv-text-secondary text-[16px] md:text-[17px] leading-[1.85] font-light max-w-xl">
                                {t(
                                    "home.presence.text",
                                    "XV Capital Partners coordonne, selon les besoins de chaque mission, un réseau de partenaires et d'experts indépendants à l'international.",
                                )}
                            </p>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4">
                                {cities.map((c) => (
                                    <div
                                        key={c}
                                        className="border-t border-xv-midnight/30 pt-4"
                                    >
                                        <div
                                            className="font-serif text-2xl md:text-[26px] text-xv-black"
                                            style={{ fontWeight: 400 }}
                                        >
                                            {c}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* INSIGHTS PREVIEW */}
            <section
                data-testid="insights-preview"
                className="bg-xv-ivory py-28 md:py-40"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
                        <div>
                            <Reveal>
                                <div className="flex items-center gap-4 mb-8 text-xv-gold">
                                    <span className="divider-gold" />
                                    <span className="overline">
                                        Articles
                                    </span>
                                </div>
                                <h2
                                    className="font-serif text-xv-black text-[40px] md:text-5xl lg:text-[60px] leading-[1.04]"
                                    style={{ fontWeight: 300 }}
                                >
                                    Notes d'analyses,
                                    <br />
                                    <em className="not-italic text-xv-midnight">
                                        écrites pour les dirigeants.
                                    </em>
                                </h2>
                            </Reveal>
                        </div>
                        <Reveal delay={0.1}>
                            <Link
                                to="/articles"
                                data-testid="insights-see-all"
                                className="inline-flex items-center gap-3 text-xv-midnight text-[11px] tracking-[0.28em] uppercase link-underline"
                            >
                                Lire toutes les notes
                                <ArrowUpRight strokeWidth={1.2} size={16} />
                            </Link>
                        </Reveal>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12 md:gap-16">
                        {articles.map((a, i) => (
                            <Reveal key={a.slug} delay={i * 0.1}>
                                <Link
                                    to={`/articles/${a.slug}`}
                                    data-testid={`insight-card-${i}`}
                                    className="group block"
                                >
                                    <div className="overflow-hidden mb-8">
                                        <img
                                            src={a.image}
                                            alt={a.title}
                                            className="w-full h-[340px] md:h-[420px] object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                                        />
                                    </div>
                                    <div className="overline text-xv-gold mb-4">
                                        {a.kicker} · {a.readTime}
                                    </div>
                                    <h3
                                        className="font-serif text-3xl md:text-4xl text-xv-black leading-[1.15] mb-4 transition-colors duration-500 group-hover:text-xv-midnight"
                                        style={{ fontWeight: 400 }}
                                    >
                                        {a.title}
                                    </h3>
                                    <p className="text-xv-text-secondary leading-[1.85] font-light max-w-lg">
                                        {a.excerpt}
                                    </p>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA FINAL */}
            <section
                data-testid="cta-final"
                className="relative bg-xv-black text-xv-ivory py-28 md:py-40 overflow-hidden"
            >
                <div className="absolute inset-0 opacity-30">
                    <img
                        src={MARBLE_IMAGE}
                        alt=""
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-xv-black/70" />
                </div>
                <div className="relative max-w-[1100px] mx-auto px-6 md:px-12 text-center">
                    <Reveal>
                        <div className="inline-flex items-center gap-4 mb-10 text-xv-goldSoft">
                            <span className="divider-gold" />
                            <span className="overline">
                                Premier entretien · Confidentiel
                            </span>
                            <span className="divider-gold" />
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2
                            className="font-serif text-[44px] md:text-6xl lg:text-[78px] leading-[1.02]"
                            style={{ fontWeight: 300 }}
                        >
                            Une opération exigeante ?{" "}
                            <em className="not-italic text-xv-goldSoft">
                                Parlons-en, à huis clos.
                            </em>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-10 text-xv-ivory/75 max-w-2xl mx-auto leading-relaxed font-light">
                            Chaque mission s'ouvre par un entretien
                            confidentiel d'évaluation. Le cabinet
                            n'accepte qu'un nombre restreint de
                            mandats chaque trimestre.
                        </p>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <Link
                            to="/contact"
                            data-testid="final-cta"
                            className="group mt-14 inline-flex items-center gap-4 px-10 py-5 bg-xv-ivory text-xv-midnightDeep text-[11px] tracking-[0.3em] uppercase hover:bg-xv-gold transition-all duration-700"
                        >
                            Initier la conversation
                            <ArrowUpRight
                                strokeWidth={1.2}
                                size={16}
                                className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                            />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
